import type { FC } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import type { Player } from '@vimeo/player';
import { Box } from '@withjoy/joykit';
import { importWithRetry } from '@shared/core/chunkRetry';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useInView } from '@shared/hooks/useInView';
import { useIsUserIdle } from '@shared/hooks/useIsUserIdle';
import { toVimeoPlayerErrorReport } from '@shared/hooks/useVimeoPlaybackSignal.utils';
import { usePrefersReducedMotion } from '@shared/hooks/usePrefersReducedMotion';
import { addError } from '@shared/utils/logger';
import { noop } from '@shared/utils/noop';
import { buildVimeoBackgroundEmbedUrl } from '@shared/utils/video';
import type { VimeoEmbedUrlOptions, VimeoVideoProps } from './VimeoVideo.types';
import { LegacyEmbedIframe } from './VimeoVideo.styles';

export const VIMEO_PLAYER_ORIGIN = 'https://player.vimeo.com';

/** Joy's Vimeo application id, carried over from the embed URLs this component replaced. */
const VIMEO_APP_ID = '58479';

// Mount the player a little before it scrolls in so the first frame is ready on arrival,
// but only play once part of it is actually on screen. The ratio is kept low on purpose: callers
// often scale the video past a cropping parent to cover it, and the clipped area counts as hidden,
// so a fully visible banner can report well under half.
const MOUNT_OBSERVER_OPTIONS: IntersectionObserverInit = { rootMargin: '200px' };
const PLAY_OBSERVER_OPTIONS: IntersectionObserverInit = { threshold: 0.2 };

/**
 * Embed URL for the optimized player (flag on): muted, chrome-less and paused, so playback is
 * driven through the player API. The `background=1` shortcut is deliberately not used: it forces
 * autoplay and looping, which would override the pause/play control.
 */
export const buildVimeoEmbedUrl = ({ videoId, privacyHash, loop }: VimeoEmbedUrlOptions): string => {
  const url = new URL(`/video/${videoId}`, VIMEO_PLAYER_ORIGIN);
  if (privacyHash) url.searchParams.set('h', privacyHash);
  url.searchParams.set('autoplay', '0');
  url.searchParams.set('loop', loop ? '1' : '0');
  url.searchParams.set('muted', '1');
  url.searchParams.set('playsinline', '1');
  url.searchParams.set('controls', '0');
  url.searchParams.set('title', '0');
  url.searchParams.set('byline', '0');
  url.searchParams.set('portrait', '0');
  url.searchParams.set('badge', '0');
  // Vimeo pauses every other player on the page when one starts; these videos play side by side.
  url.searchParams.set('autopause', '0');
  url.searchParams.set('dnt', '1');
  url.searchParams.set('app_id', VIMEO_APP_ID);
  return url.href;
};

/**
 * Embed URL for the legacy embed (flag off): Vimeo's background mode, which autoplays, loops,
 * mutes and hides the chrome on its own, via the shared {@link buildVimeoBackgroundEmbedUrl}.
 * This is the URL most of the replaced hand-written embeds used.
 */
export const buildLegacyVimeoEmbedUrl = ({ videoId, privacyHash, loop }: VimeoEmbedUrlOptions): string => {
  const url = new URL(`/video/${videoId}`, VIMEO_PLAYER_ORIGIN);
  if (privacyHash) url.searchParams.set('h', privacyHash);
  // Set before the helper, which only fills in params the URL doesn't already carry.
  url.searchParams.set('loop', loop ? '1' : '0');
  url.searchParams.set('autopause', '0');
  url.searchParams.set('app_id', VIMEO_APP_ID);
  // The input is always a player.vimeo.com URL, so the helper never returns null here.
  return buildVimeoBackgroundEmbedUrl(url.href) ?? url.href;
};

type LegacyVimeoEmbedProps = Readonly<{
  embedUrl: string;
  title: string;
  decorative: boolean;
  posterUrl: string | undefined;
  posterFit: 'cover' | 'contain';
  /** Mount the iframe; until then only the poster (if any) renders. */
  isMounted: boolean;
  /** Mirror the replaced iframe's `loading="lazy"`. */
  lazy: boolean;
}>;

/**
 * The flag-off rollback: the plain autoplaying embed the call sites used before this component.
 * No player API, viewport gating, idle pausing, reveal fade or reduced-motion handling, so turning
 * the flag off restores the old playback exactly. The poster sits behind the iframe as a still
 * background, the way the old embeds layered their thumbnails.
 */
const LegacyVimeoEmbed: FC<LegacyVimeoEmbedProps> = ({ embedUrl, title, decorative, posterUrl, posterFit, isMounted, lazy }) => (
  <>
    {posterUrl && (
      <Box
        as="img"
        src={posterUrl}
        alt=""
        aria-hidden
        position="absolute"
        inset={0}
        width="100%"
        height="100%"
        objectFit={posterFit}
        pointerEvents="none"
      />
    )}
    {isMounted && (
      <LegacyEmbedIframe
        src={embedUrl}
        title={title}
        aria-hidden={decorative || undefined}
        tabIndex={-1}
        // Only where the replaced iframe was lazy: elsewhere `lazy` would change when flag-off videos load.
        loading={lazy ? 'lazy' : undefined}
        allow="autoplay; fullscreen; picture-in-picture"
        referrerPolicy="strict-origin-when-cross-origin"
      />
    )}
  </>
);

type VimeoVideoPlayerProps = Readonly<{
  embedUrl: string;
  title: string;
  decorative: boolean;
  posterUrl: string | undefined;
  posterFit: 'cover' | 'contain';
  loop: boolean;
  /** Mount the iframe; until then only the poster (if any) renders. */
  isMounted: boolean;
  /** On screen, user active, motion allowed. The player still holds back once a non-looping video ends. */
  canPlay: boolean;
  prefersReducedMotion: boolean;
}>;

/**
 * One embed and its playback state. The parent renders it with `key={embedUrl}`, so a different
 * video (or loop setting) remounts it with fresh state: hidden behind its poster until its
 * own first frame, never showing the previous video's ready/played/ended state for a render.
 */
const VimeoVideoPlayer: FC<VimeoVideoPlayerProps> = ({ embedUrl, title, decorative, posterUrl, posterFit, loop, isMounted, canPlay, prefersReducedMotion }) => {
  const [iframe, setIframe] = useState<HTMLIFrameElement | null>(null);
  const [player, setPlayer] = useState<Player | null>(null);
  // `ready` means Vimeo has drawn the still frame; before that the iframe is a black box.
  const [isReady, setIsReady] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [hasEnded, setHasEnded] = useState(false);

  useEffect(() => {
    if (!iframe) return undefined;
    let cancelled = false;
    let connected: Player | null = null;
    const onReady = () => setIsReady(true);
    const onPlaying = () => {
      setIsReady(true);
      setHasPlayed(true);
    };
    const onEnded = () => setHasEnded(true);
    // Vimeo sends a plain `{ name, message, method }` object; the helper turns it into a named Error.
    const onPlayerError = (payload: unknown) => {
      const { error, attributes } = toVimeoPlayerErrorReport(payload);
      addError(error, { ...attributes, context: 'VimeoVideo#playerError' });
    };

    importWithRetry(
      () =>
        import(
          /* webpackChunkName: "vimeo-player" */
          '@vimeo/player'
        ),
      'vimeo-player',
    )
      .then(({ default: VimeoPlayer }) => {
        if (cancelled) return;
        connected = new VimeoPlayer(iframe);
        connected.on('playing', onPlaying);
        connected.on('ended', onEnded);
        connected.on('error', onPlayerError);
        connected.ready().then(() => {
          if (!cancelled) onReady();
        }, noop);
        setPlayer(connected);
      })
      .catch(error => {
        addError(error, { context: 'VimeoVideo#loadPlayerLib' });
        // No API means no playback signal: show the player rather than hide a playing video forever.
        if (!cancelled) onPlaying();
      });

    return () => {
      cancelled = true;
      setPlayer(null);
      // No destroy(): the lib would remove the React-owned iframe. off() is enough.
      connected?.off('playing', onPlaying);
      connected?.off('ended', onEnded);
      connected?.off('error', onPlayerError);
    };
  }, [iframe]);

  const shouldPlay = canPlay && !(hasEnded && !loop);

  useEffect(() => {
    if (!player) return;
    // The player queues commands until it is ready. A rejected play() (autoplay blocked, e.g.
    // iOS Low Power Mode) leaves the poster or still frame up, which is the intended fallback.
    (shouldPlay ? player.play() : player.pause()).catch(noop);
  }, [player, shouldPlay]);

  const showPoster = !!posterUrl && (prefersReducedMotion || !hasPlayed);

  return (
    <>
      {isMounted && (
        <Box
          as="iframe"
          ref={setIframe}
          src={embedUrl}
          title={title}
          aria-hidden={decorative || undefined}
          // No controls to reach, and pointer events are off; keep Tab out of the player too.
          tabIndex={-1}
          allow="autoplay; fullscreen; picture-in-picture"
          referrerPolicy="strict-origin-when-cross-origin"
          position="absolute"
          inset={0}
          width="100%"
          height="100%"
          border="none"
          pointerEvents="none"
          opacity={isReady ? 1 : 0}
          transition="opacity 0.5s ease"
        />
      )}
      {showPoster && (
        <Box
          as="img"
          src={posterUrl}
          alt=""
          aria-hidden
          position="absolute"
          inset={0}
          width="100%"
          height="100%"
          objectFit={posterFit}
          pointerEvents="none"
        />
      )}
    </>
  );
};

/**
 * A muted, looping Vimeo video without player chrome — value-prop loops, dialog and banner art.
 *
 * With the `vimeoPlaybackOptimizationEnabled` flag on, the video only spends bandwidth while
 * someone could be watching it: it plays while it is on screen and the user has
 * been active in the last 30s, pauses otherwise, and resumes on the next input if still on screen.
 * `paused` holds it from outside, e.g. while a dialog covers it.
 * The player mounts only once the video nears the viewport, `posterUrl` covers it until a frame
 * actually plays, and reduced-motion users never get playback: they see the poster, or Vimeo's own
 * still frame when there is none.
 *
 * With the flag off it renders {@link LegacyVimeoEmbed} instead: the autoplaying background-mode
 * embed the call sites used before, mounted right after hydration, with none of the above. That
 * makes the flag a full playback rollback; only the call sites' framing and markup are unflagged.
 * Nothing mounts while the flag is still loading, so the player is never loaded twice.
 *
 * Not for a couple's own header video: that has its own stall handling (`useVimeoPlaybackSignal`).
 */
export const VimeoVideo: FC<VimeoVideoProps> = ({
  videoId,
  privacyHash,
  title,
  posterUrl,
  posterFit = 'cover',
  loop = true,
  aspectRatio,
  borderRadius,
  backgroundColor = 'transparent',
  decorative = false,
  paused = false,
  lazy = false,
}) => {
  const [isOptimized, isFlagLoading] = useIsFeatureEnabled('vimeoPlaybackOptimizationEnabled');
  const prefersReducedMotion = usePrefersReducedMotion();
  const isUserIdle = useIsUserIdle(isOptimized);
  const { ref: mountRef, inView: isNearViewport } = useInView<HTMLDivElement>(MOUNT_OBSERVER_OPTIONS);
  const { ref: playRef, inView: isOnScreen } = useInView<HTMLDivElement>(PLAY_OBSERVER_OPTIONS);
  const containerRef = useCallback(
    (element: HTMLDivElement | null) => {
      mountRef(element);
      playRef(element);
    },
    [mountRef, playRef],
  );

  const [isMounted, setIsMounted] = useState(false);

  // Mount once and keep it: unmounting on scroll-away would re-download the player each time.
  // Wait for the flag: it picks the embed, and switching embeds would load the player twice.
  // Legacy mounts on the first client effect (never during SSR); optimized waits for the viewport.
  useEffect(() => {
    if (!isFlagLoading && (!isOptimized || isNearViewport)) setIsMounted(true);
  }, [isFlagLoading, isOptimized, isNearViewport]);

  const containerProps = {
    position: 'relative',
    width: '100%',
    height: aspectRatio ? undefined : '100%',
    aspectRatio,
    borderRadius,
    overflow: 'hidden',
    backgroundColor,
  } as const;

  if (!isOptimized) {
    return (
      <Box {...containerProps}>
        <LegacyVimeoEmbed
          embedUrl={buildLegacyVimeoEmbedUrl({ videoId, privacyHash, loop })}
          title={title}
          decorative={decorative}
          posterUrl={posterUrl}
          posterFit={posterFit}
          isMounted={isMounted}
          lazy={lazy}
        />
      </Box>
    );
  }

  const embedUrl = buildVimeoEmbedUrl({ videoId, privacyHash, loop });
  const canPlay = !paused && !prefersReducedMotion && isOnScreen && !isUserIdle;

  return (
    <Box
      ref={containerRef}
      {...containerProps}
    >
      <VimeoVideoPlayer
        key={embedUrl}
        embedUrl={embedUrl}
        title={title}
        decorative={decorative}
        posterUrl={posterUrl}
        posterFit={posterFit}
        loop={loop}
        isMounted={isMounted}
        canPlay={canPlay}
        prefersReducedMotion={prefersReducedMotion}
      />
    </Box>
  );
};
